import * as React from 'react';

import omit from 'lodash/omit';
import compose from 'recompose/compose';

import Retracked from 'js/app/retracked';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';

import { StyleSheet, css } from '@coursera/coursera-ui';

import { AUTHENTICATION_MODAL_MODES } from 'bundles/authentication/constants';
import Modal from 'bundles/authentication/shared/Modal';
import ModalV2 from 'bundles/authentication/shared/ModalV2';
import AuthenticationContent from 'bundles/authentication/shared/components/authentication-modal/AuthenticationContent';
import type { AuthModalContext } from 'bundles/authentication/shared/types/sharedTypes';
import { isVisualImprovementsToAuthModalsEnabled } from 'bundles/authentication/utils/experimentUtils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type { AuthenticationContentBaseProps, AuthenticationModalBaseProps } from 'bundles/user-account/types';

import _t from 'i18n!nls/authentication';

export type PropsFromCaller = {
  /**
   * Use this prop to specify any additional variables, functions, etc. that a specific view in <AuthenticationContent>
   * might need
   */
  context?: AuthModalContext;
} & AuthenticationContentBaseProps &
  AuthenticationModalBaseProps;

type AuthRouter = {
  location: {
    pathname: string;
    protocol: string;
    hostname: string;
  };
};

type Props = PropsFromCaller & { router: AuthRouter & InjectedRouter };

type State = {
  name: string;
  isOpen: boolean;
};

const styles = StyleSheet.create({
  body: {
    overflow: 'hidden',
  },
});

export class AuthenticationModal extends React.LegacyComponentWithChildren<Props, State> {
  state = {
    name: _t('Authentication modal'),
    isOpen: false,
  };

  componentDidMount() {
    const { defaultMode, router } = this.props;

    const tracker = Retracked.makeTracker({
      namespace: 'open_course.user', // for backwards compatibility with userAuthorization
    });

    const mode = defaultMode || router.location.query.authMode;

    tracker(mode === AUTHENTICATION_MODAL_MODES.LOGIN ? 'login_modal.open' : 'signup_modal.open');

    requestAnimationFrame(() => {
      this.setState({ isOpen: true });
    });
  }

  close = () => {
    this.setState({ isOpen: false });
  };

  onClose = () => {
    const { onCloseRedirectURL, router, onClose, utilizeWebRedirectOnClose } = this.props;

    onClose?.();

    if (onCloseRedirectURL) {
      // I'm not using the redirect authentication action because I want the caller to be able to pass
      // in just a path and not a full url.
      if (utilizeWebRedirectOnClose) {
        window.location.replace(onCloseRedirectURL);
      } else {
        router.push({ pathname: onCloseRedirectURL });
      }
      return;
    }

    const currentPath = router?.location.pathname;
    const query = omit(router.location.query, 'authMode');

    router.push({ pathname: currentPath, query });
  };

  render() {
    const {
      defaultMode,
      redirectTo: defaultRedirectTo,
      router,
      context: contextFromProps,
      isEnterprise,
      ...props
    } = this.props;
    const { name, isOpen } = this.state;
    const { query } = router.location;
    const {
      authProvider,
      verificationEmailRedirectUrl,
      authType,
      redirectTo: redirectToFromQuery,
      r,
      action,
      serviceCode,
      completeMode,
    } = query;
    const mode = defaultMode || router.location.query.authMode;

    const redirectTo = defaultRedirectTo || redirectToFromQuery || r;
    const context = {
      action,
      accountType: serviceCode,
      authProvider,
      mode: completeMode,
      authMode: mode,
      service: authType,
      verificationEmailRedirectUrl,
      isEnterprise,
      ...contextFromProps,
    };

    const renderModal = () => {
      if (isVisualImprovementsToAuthModalsEnabled()) {
        return (
          <ModalV2 {...props} contentLabel={name} isOpen={isOpen} onAfterClose={this.onClose}>
            <AuthenticationContent {...props} context={context} defaultMode={mode} redirectTo={redirectTo} />
          </ModalV2>
        );
      }
      return (
        <Modal
          {...props}
          bodyOpenClassName={css(styles.body).className}
          contentLabel={name}
          isOpen={isOpen}
          onAfterClose={this.onClose}
          onRequestClose={this.close}
          shouldCloseOnOverlayClick={false}
        >
          <AuthenticationContent {...props} context={context} defaultMode={mode} redirectTo={redirectTo} />
        </Modal>
      );
    };

    return (
      <TrackedDiv
        className="rc-AuthenticationModal"
        trackingName="authentication_modal"
        data-e2e="auth-modal"
        data-testid="auth-modal"
        trackClicks={false}
        withVisibilityTracking
      >
        {renderModal()}
      </TrackedDiv>
    );
  }
}

export default compose<Props, PropsFromCaller>(
  connectToRouter<Props, PropsFromCaller>((router) => ({ router })),
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'authentication',
    },
  }))
)(AuthenticationModal);
